import { bffFetch, bffJson } from "@/shared/api/client";
import { clientLinkQuery, linkOwnerCorporationId } from "@/domain/clients/individual-client";
import { readParty, type PartyWire } from "@/domain/clients/party";

interface CorporationClientRow {
  id?: string;
  corporation_id?: string;
  primary_financial_advisor_id?: string;
  start_at_inclusive?: string | null;
}

export interface CorporationClientSubject {
  corporationId: string;
  corporationClientId: string;
  name: string;
  organisationNumber: string | null;
  startAt?: string | null;
  primaryFinancialAdvisorId?: string | null;
}

function organisationNumberOf(record: PartyWire): string | null {
  const ids = record.identification_numbers;
  if (!Array.isArray(ids) || ids.length === 0) return null;
  const organisationNumber =
    ids.find((identifier) => identifier.type === "ORGANIZATION_NUMBER") ?? ids[0];
  const value = (organisationNumber.value ?? "").trim();
  return value === "" ? null : value;
}

async function hydrate(row: CorporationClientRow): Promise<CorporationClientSubject | null> {
  const corporationId = row.corporation_id;
  const corporationClientId = row.id;
  if (!corporationId || !corporationClientId) return null;
  try {
    const corporation = await readParty("corporation", corporationId);
    if (corporation === null) return null;
    return {
      corporationId,
      corporationClientId,
      name:
        typeof corporation.name === "string" && corporation.name.trim() !== ""
          ? corporation.name.trim()
          : corporationId,
      organisationNumber: organisationNumberOf(corporation),
      startAt: row.start_at_inclusive ?? null,
      primaryFinancialAdvisorId: row.primary_financial_advisor_id ?? null,
    };
  } catch {
    return null;
  }
}

export async function listCorporationClients(
  advisorFinancialAdvisorId: string,
): Promise<CorporationClientSubject[]> {
  const json = await bffJson<unknown>(
    `v1/corporation-client?${clientLinkQuery({
      ownerCorporationId: await linkOwnerCorporationId(),
      financialAdvisorId: advisorFinancialAdvisorId,
    })}`,
  );
  const rows = Array.isArray(json) ? (json as CorporationClientRow[]) : [];
  const matchingRows = rows.filter(
    (row) =>
      typeof row.id === "string" &&
      row.id !== "" &&
      typeof row.corporation_id === "string" &&
      row.corporation_id !== "" &&
      row.primary_financial_advisor_id === advisorFinancialAdvisorId,
  );
  const hydrated = await Promise.all(matchingRows.map(hydrate));
  return hydrated.filter((subject): subject is CorporationClientSubject => subject !== null);
}

export async function readCorporationClientSubject(
  corporationClientId: string,
): Promise<CorporationClientSubject | null> {
  const response = await bffFetch(
    `v1/corporation-client/${encodeURIComponent(corporationClientId)}`,
  );
  if (response.status === 404) return null;
  if (!response.ok)
    throw new Error(`/v1/corporation-client/${corporationClientId} failed: ${response.status}`);
  const row = (await response.json()) as CorporationClientRow;
  return hydrate({ ...row, id: row.id ?? corporationClientId });
}
